import {
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy,
  computed,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft,
  lucideKeyboard,
  lucideScanLine,
  lucideTicket,
  lucideTriangleAlert,
  lucideUsers,
  lucideX,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTabsImports } from '@spartan-ng/helm/tabs';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { LoggerService } from '../../services/logger.service';
import { VoucherService } from '../../services/voucher.service';
import { BrowserMultiFormatReader, Result } from '@zxing/library';
import { Observable, map } from 'rxjs';
import type { PublicVoucherVerification, Voucher } from '../../services/voucher.service';
import { CompanyService } from '../../services/company.service';
import { TokenStorageService } from '../../services/token-storage.service';
import { VoucherBalanceCardComponent } from '../../components/voucher-balance-card/voucher-balance-card.component';
import {
  RedemptionFlowComponent,
  RedemptionResult,
} from '../../components/redemption-flow/redemption-flow.component';
import { VoucherTimelineComponent } from '../../components/voucher-timeline/voucher-timeline.component';

@Component({
  selector: 'app-redeem',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmFieldImports,
    HlmInputImports,
    HlmSkeletonImports,
    HlmSpinnerImports,
    HlmTabsImports,
    HlmTypographyImports,
    VoucherBalanceCardComponent,
    RedemptionFlowComponent,
    VoucherTimelineComponent,
  ],
  providers: [
    provideIcons({
      lucideArrowLeft,
      lucideKeyboard,
      lucideScanLine,
      lucideTicket,
      lucideTriangleAlert,
      lucideUsers,
      lucideX,
    }),
  ],
  templateUrl: './redeem.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: [],
})
export class RedeemComponent implements AfterViewInit, OnDestroy {
  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;

  voucherForm: FormGroup;
  /** Own vouchers come by code (with recipient), scanned ones from the public lookup. */
  voucher: Voucher | PublicVoucherVerification | null = null;
  verificationKey: string = '';
  /** Lookup failed (not found, camera, …). Redemption errors live in the flow. */
  error: string = '';
  isLoading: boolean = false;
  /** Which input method the box currently shows. */
  mode: 'scan' | 'code' = 'scan';
  isScanning: boolean = false;

  isRedeeming: boolean = false;
  redemptionError: string = '';
  redemptionResult: RedemptionResult | null = null;

  // Franchise: when voucher belongs to a different (central) company in the same group
  isFranchiseVoucher: boolean = false;

  /** A network only sells; its members redeem. It gets a hint instead of the scanner. */
  readonly isNetwork = computed(() => !!this.companyService.company()?.is_group_admin);

  private currentCompanyId: string | null = null;
  /** A QR code that wasn't found: ignored while it stays in front of the camera. */
  private failedKey: string | null = null;
  private codeReader: BrowserMultiFormatReader;

  constructor(
    private formBuilder: FormBuilder,
    private voucherService: VoucherService,
    private router: Router,
    private logger: LoggerService,
    private tokenStorage: TokenStorageService,
    private companyService: CompanyService,
  ) {
    this.currentCompanyId = this.tokenStorage.getPublicCompanyId();
    this.voucherForm = this.formBuilder.group({
      code: ['', [Validators.required, Validators.minLength(8)]],
    });

    this.codeReader = new BrowserMultiFormatReader();
  }

  ngAfterViewInit() {
    // Start scanner automatically since it's shown by default
    setTimeout(() => {
      if (this.mode === 'scan' && !this.isNetwork()) {
        this.startScanner();
      }
    }, 100);
  }

  ngOnDestroy() {
    this.stopScanner();
  }

  /** Recipient for own vouchers; scanned ones only carry the shop name. */
  get voucherSubtitle(): string | null {
    const v = this.voucher;
    if (!v) return null;
    if ('recipient_name' in v) return v.recipient_name || v.recipient_email || null;
    return v.company_name;
  }

  /** Kept on screen after the last euro is redeemed, so the confirmation stays visible. */
  get showRedemption(): boolean {
    return (
      !!this.redemptionResult || (this.voucher?.status === 'active' && this.voucher.amount > 0)
    );
  }

  getErrorTitle(): string {
    if (this.error.startsWith('Dieser Gutschein existiert nicht')) {
      return 'Gutschein nicht gefunden';
    }
    if (this.error.startsWith('Dieser Gutschein gehört')) {
      return 'Kein Zugriff';
    }
    if (this.error.startsWith('Die Kamera')) {
      return 'Kamera nicht verfügbar';
    }
    return 'Fehler';
  }

  private describeError(error: any): string {
    this.logger.error('Error:', error);
    if (error.status === 401) {
      this.router.navigate(['/login']);
      return 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.';
    }
    if (error.status === 404) {
      return 'Dieser Gutschein existiert nicht. Bitte prüfe den Code und versuche es erneut.';
    }
    if (error.status === 403) {
      return error.error?.detail || 'Dieser Gutschein gehört zu einem anderen Unternehmen.';
    }
    return (
      error.error?.detail || 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es erneut.'
    );
  }

  setMode(mode: 'scan' | 'code') {
    if (this.mode === mode) return;
    this.mode = mode;
    this.error = '';

    if (mode === 'scan') {
      // The video element only exists once the tab content is rendered.
      setTimeout(() => this.startScanner(), 100);
    } else {
      this.stopScanner();
    }
  }

  async startScanner() {
    try {
      this.isScanning = true;

      if (!this.videoElement?.nativeElement) {
        throw new Error('Video element not found');
      }

      await this.codeReader.decodeFromVideoDevice(
        null,
        this.videoElement.nativeElement,
        (result: Result | null) => {
          if (result) {
            // Extract verification key from URL or use as-is if it's just the key
            const key = this.extractVerificationKey(result.getText());
            if (key === this.failedKey) return;
            this.verificationKey = key;
            this.stopScanner();
            this.lookupVoucherByVerificationKey();
          }
        },
      );
    } catch (error) {
      this.logger.error('Error starting scanner:', error);
      this.error =
        'Die Kamera konnte nicht gestartet werden. Bitte achte darauf, dass die Kamerarechte gewährt werden und versuche es erneut, oder gib den Gutscheincode ein.';
      this.isScanning = false;
    }
  }

  stopScanner() {
    this.codeReader.reset();
    this.isScanning = false;
  }

  /** Back to the scanner (or code field) for the next voucher. */
  startOver() {
    this.voucher = null;
    this.verificationKey = '';
    this.error = '';
    this.redemptionError = '';
    this.redemptionResult = null;
    this.isFranchiseVoucher = false;
    this.failedKey = null;
    this.voucherForm.reset({ code: '' });
    if (this.mode === 'scan') {
      // The box is rendered again only after this change detection pass.
      setTimeout(() => this.startScanner(), 100);
    }
  }

  extractVerificationKey(input: string): string {
    // Check if input is a URL containing /verify/
    if (input.includes('/verify/')) {
      const parts = input.split('/verify/');
      return parts[parts.length - 1];
    }
    // Otherwise, assume it's already a verification key
    return input;
  }

  lookupVoucher() {
    if (this.voucherForm.invalid || this.isLoading) return;
    this.startLookup();

    // Use authenticated endpoint to get voucher by code
    this.voucherService.getVoucherByCode(this.voucherForm.get('code')?.value).subscribe({
      next: (data) => {
        this.voucher = data;
        this.verificationKey = data.verification_key || '';
        this.isLoading = false;
      },
      error: (error) => {
        this.error = this.describeError(error);
        this.isLoading = false;
      },
    });
  }

  lookupVoucherByVerificationKey() {
    if (!this.verificationKey || this.isLoading) return;
    this.startLookup();

    this.voucherService.verifyVoucherPublic(this.verificationKey).subscribe({
      next: (data) => {
        this.voucher = data;
        this.voucherForm.patchValue({ code: data.code });

        // Detect franchise: voucher's company is different from logged-in company
        const voucherCompanyId = data.public_company_id;
        this.isFranchiseVoucher =
          !!voucherCompanyId &&
          !!this.currentCompanyId &&
          voucherCompanyId !== this.currentCompanyId;

        this.isLoading = false;
      },
      error: (error) => {
        this.error = this.describeError(error);
        this.isLoading = false;
        // Keep scanning: the box is back, with a fresh video element.
        this.failedKey = this.verificationKey;
        if (this.mode === 'scan') setTimeout(() => this.startScanner(), 100);
      },
    });
  }

  private startLookup() {
    this.isLoading = true;
    this.error = '';
    this.voucher = null;
    this.redemptionError = '';
    this.redemptionResult = null;
    this.isFranchiseVoucher = false;
  }

  /** New balance and history after a redemption, from the same source as the lookup. */
  private refreshVoucher() {
    const voucher = this.voucher;
    if (!voucher) return;
    const source: Observable<Voucher | PublicVoucherVerification> =
      'recipient_name' in voucher
        ? this.voucherService.getVoucherByCode(voucher.code)
        : this.voucherService.verifyVoucherPublic(this.verificationKey);
    source.subscribe({
      next: (data) => (this.voucher = data),
      error: (error) => this.logger.error('Error refreshing voucher:', error),
    });
  }

  /** `amountInCents` null: the whole balance. */
  redeem(amountInCents: number | null) {
    if (!this.voucher || this.isRedeeming || !this.verificationKey) return;

    const balance = this.voucher.amount;
    this.isRedeeming = true;
    this.redemptionError = '';

    // A full redemption answers without the balance: it is 0 then.
    const remaining$: Observable<number> =
      amountInCents === null
        ? this.voucherService.redeemVoucher(this.verificationKey).pipe(map(() => 0))
        : this.voucherService
            .redeemPartialVoucher(this.verificationKey, amountInCents)
            .pipe(map((response) => response.remaining_amount));

    remaining$.subscribe({
      next: (remaining) => {
        this.redemptionResult = { redeemed: amountInCents ?? balance, remaining };
        this.isRedeeming = false;
        this.refreshVoucher();
      },
      error: (error) => {
        this.redemptionError = this.describeError(error);
        this.isRedeeming = false;
      },
    });
  }

  continueRedemption() {
    this.redemptionResult = null;
    this.redemptionError = '';
  }
}
